/**
 * Copyright 2019-2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  getStoredYmmeActionSource,
  removeYmmeActionSource,
} from '@/features/ymme/utils/ymmeActionSourceHelper';
import { getAndResetAdditionalQuestionsFlag } from '@/features/ymme/utils/additionalQuestionsTracker';
import { type InteractionLocation } from '@/types/analytics';
import { clickTrack } from './analytics/clickTrack';
import { ymmeActionSources } from './ymmeHelpers';
import root from 'window-or-global';
import { type HeaderData } from '@/features/header';
import { getStoredInteractionLocation } from '@/features/ymme/utils/ymmeLocationHelper';
import type { AcesFlags } from '@/features/ymme/utils/acesFlags';
import { formatBaseVehicle, formatVehicleQuestions } from './analytics/vehicleFormatter';
import { type VehicleSummary } from '@/types';

type AnalyticsVehicleData = {
  userVehicleCount: string | undefined;
  vehicleTypeName?: string | undefined;
  year: string | undefined;
  make: string | undefined;
  model: string | undefined;
  subModel?: string | undefined;
  engine: string | undefined;
  id: string | undefined;
  interactionLocation: InteractionLocation;
  actionSource: string | undefined;
  wasShownAdditionalQuestions?: boolean;
};

export const addVehicle = (
  vehicleData: AnalyticsVehicleData,
  acesFlags?: AcesFlags,
  vehicleMap?: VehicleSummary,
) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
  const searchKeyword = (root.utag_data?.searchKeyword ?? '') as string;

  // Check if vehicle was added from search flow (not just while on search page)
  const isSearchContext = vehicleData.actionSource === ymmeActionSources.vehicleSearch;

  // Use "Internal Search" for vehicle_interaction_location if from search
  const vehicleInteractionLocation: InteractionLocation = isSearchContext
    ? 'Internal Search'
    : vehicleData.interactionLocation || 'URL';

  // Use eventType for customLinkName if from search, otherwise use actionSource
  const customLinkName = isSearchContext
    ? `${pageType}:addVehicle`
    : `${pageType}:${vehicleData.actionSource || ymmeActionSources.directURL}`;

  const { acesEnabled, acesTransitionPhase, partsProFlag } = acesFlags || {};

  // Define interaction locations that CANNOT have AQs (much shorter list)
  const interactionLocationsWithoutAQs: InteractionLocation[] = [
    'VIN-Decoder',
    'Scan Vin',
    'VIN',
    'License Plate',
  ];

  // Check if this interaction location can have AQs
  const canHaveAQs =
    acesEnabled &&
    partsProFlag === false &&
    !interactionLocationsWithoutAQs.includes(vehicleInteractionLocation);

  const wasAnsweredAdditionalQuestions =
    Boolean(vehicleData?.engine) || Boolean(vehicleData?.subModel);

  clickTrack({
    eventType: 'addVehicle',
    ...(canHaveAQs &&
      acesTransitionPhase === false && {
        additionalQuestionsAnswered: wasAnsweredAdditionalQuestions,
      }),
    userVehicleCount: vehicleData.userVehicleCount,
    ...(acesEnabled
      ? {
          vehicleInformation: formatVehicleInformation(vehicleMap),
          Submodel: vehicleData.subModel ?? '',
          Engine: vehicleData.engine ?? '',
          baseVehicle: formatBaseVehicle(vehicleMap),
          vehicleQuestions: formatVehicleQuestions(vehicleMap),
        }
      : {
          vehicleYear: vehicleData.year,
          vehicleMake: vehicleData.make,
          vehicleModel: vehicleData.model,
          vehicleEngine: vehicleData.engine,
          vehicleId: vehicleData.id,
        }),
    vehicle_interaction_location: isSearchContext
      ? vehicleInteractionLocation
      : canHaveAQs && acesTransitionPhase === false
        ? `${vehicleInteractionLocation} ${'with Additional Questions'}`
        : vehicleInteractionLocation,
    ...(isSearchContext && { searchKeyword }),
    pageName,
    pageType,
    siteSection,
    customLinkName: customLinkName,
  });
  removeYmmeActionSource();
};

/**
 * Formats vehicleInformation for ACES analytics with any unanswered optional questions
 * interpolated as empty strings
 * @return 'Vehicle Type|Year|Make|Model|SubMode|Engine'
 */
const formatVehicleInformation = (vehicleData: VehicleSummary | undefined) => {
  if (vehicleData) {
    const { year = '', make = '', model = '', vehicleTypeName = '' } = vehicleData;
    return `${vehicleTypeName}|${year}|${make}|${model}`;
  } else {
    return `|||`;
  }
};

export const trackAddVehicle = ({
  isAddByURL,
  headerData,
  acesFlags,
}: {
  isAddByURL: boolean;
  headerData?: HeaderData;
  acesFlags?: AcesFlags;
}) => {
  const { vehicleCount, year, make, model, subModel, engine, vehicleTypeName, catalogVehicleId } =
    headerData?.vehicleMap || {};

  // Get and reset the additional questions flag
  const wasShownAdditionalQuestions = getAndResetAdditionalQuestionsFlag();

  addVehicle(
    {
      userVehicleCount: vehicleCount,
      vehicleTypeName,
      year,
      make,
      model,
      subModel,
      engine,
      id: catalogVehicleId,
      interactionLocation: isAddByURL ? 'URL' : getStoredInteractionLocation(),
      actionSource: getStoredYmmeActionSource(),
      wasShownAdditionalQuestions,
    },
    acesFlags,
    headerData?.vehicleMap,
  );
};
